import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { store } from 'redux-rest-reducer';
import { connect } from 'react-redux';
import { getAll as getProgramAnalytics } from 'actions/program-analytics';
import styled from 'styled-components';
import { Text, Row, Col, Separator } from '@cuemath/comps';
import { CSVLink } from 'react-csv';
import Chart from './worksheet-pie-chart';
import StudentTable from './student-table';
import ChapterTable from './chapters-table';
import COLORS from 'constants/colors';
import CompletionTable from './completion-table';
import SpeedStatTable from './speed-table';
import OptionalSheetTable from './optional-sheet-table';

const LOCATION = {
  CW: 'INCLASS',
  HW: 'ATHOME',
  ANY: 'ANY',
};

const TYPES = {
  OVERALL: 'overall',
  MANDATORY: 'mandatory',
  ADVANCED: 'ADVANCED',
  SUBJECTIVE: 'SUBJECTIVE',
  PRACTICE: 'PRACTICE',
  OTHERS: 'OTHERS',
};

const Wrapper = styled.div`
  width: 90%;
  margin: auto;
`;

const WrapperHeadingText = styled(Text)`
  font-size: 38px;
  font-weight: 600;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 3px;
  width: 120px;
  padding: 15px;
  margin-right: 40px;
  border: 1px solid ${COLORS.BLACK};
  border-radius: 20px;
  text-align: center;
`;

const CsvIcon = styled(CSVLink)`
  color: ${COLORS.BLACK};
  margin-left: ${({ marginLeft }) => marginLeft}px;
  margin-top: ${({ marginTop }) => marginTop}px;
`;

const WorksheetLocationWrapper = styled(Col)`
  justify-content: center;
  align-items: center;
  border: 2px solid ${COLORS.BLACK};
`;

const HeadingWrapper = styled(Row)`
  gap: 12px;
  align-items: center;
  font-size: 1.1em;
  i {
    :hover {
      cursor: pointer;
    }
  }
`;

const HeadingText = styled(Text)`
  font-size: 28px;
  font-weight: 600;
  margin: 30px 0px;
`;

const SubHeading = styled(Text)`
  font-size: 16px;
  margin-top: ${({ top }) => top || null};
  margin-bottom: ${({ bottom }) => bottom || 15}px;
`;

const WorksheetContainer = styled(Row)`
  gap: 30px;
`;

const WorksheetDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px;
`;

const ProgressByLocation = styled.div`
  width: 100%;
  margin: auto;
  height: 20px;
  background: ${({ cw, hw }) =>
    `linear-gradient(to right, ${COLORS.CERULEAN} , ${COLORS.CERULEAN} ${cw}%, ${
      COLORS.GREEN
    } ${hw}%, ${COLORS.GREEN} ${hw + cw}%, ${COLORS.SILVERCHALICE} ${hw + cw}%)`};
  margin-bottom: 10px;
`;

const LocationTypes = styled(Row)`
  width: 80%;
  margin: 10px auto;
  justify-content: space-between;
  align-items: center;
`;

const OptionalWorksheetWrapper = styled(Row)`
  width: 200px;
  height: 70px;
  justify-content: center;
  align-items: center;
  gap: 15px;
  border: 2px solid ${COLORS.BLACK};
`;

const ColBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

function getCountByLocation(type, data = []) {
  const { count } = (data && data.find(item => item.location === type)) || {};

  return count;
}

function getLocationBarPercentage(data) {
  let total = data && data.reduce((acc, { count }) => acc + count, 0);

  return {
    CW: (getCountByLocation(LOCATION.CW, data) / total) * 100,
    HW: (getCountByLocation(LOCATION.HW, data) / total) * 100,
    ANY: (getCountByLocation(LOCATION.ANY, data) / total) * 100,
  };
}

const studentCsv = data => {
  return (
    data &&
    data.map(({ position, type, total, active }) => ({
      id: position,
      country: type,
      assigned_students: total,
      active_students: active,
    }))
  );
};

const optionalSheetCsv = data => {
  return (
    data &&
    data.map(({ type, completion_percentage, students_percentage }) => ({
      type: type,
      completion_percentage: completion_percentage,
      students_percentage: students_percentage,
    }))
  );
};

const optionalSheetDataFormat = optionalSheets => {
  const optionalSheetData = [];

  optionalSheets &&
    Object.entries(optionalSheets).forEach(([key, val]) => {
      val.map(obj => {
        const { ADVANCED, SUBJECTIVE, PRACTICE, OTHERS } = TYPES;
        obj.type = key;
        switch (key) {
          case ADVANCED:
            obj.order = 0;
            break;
          case SUBJECTIVE:
            obj.order = 1;
            break;
          case PRACTICE:
            obj.order = 2;
            break;
          case OTHERS:
            obj.order = 3;
            break;
          default:
            break;
        }
        optionalSheetData.push(obj);

        return obj;
      });
    });

  return optionalSheetData;
};

class ProgramAnalytics extends PureComponent {
  componentDidMount() {
    const { actions, programId } = this.props;
    actions.getProgramAnalytics({ programId });
  }

  render() {
    const { programAnalytics, programId } = this.props;
    const programAnalyticsList = store.getAll(programAnalytics, { programId });
    const programAnalyticsData = programAnalyticsList && programAnalyticsList.data;
    const {
      program_name: programName,
      program_code: programCode,
      total_chapters: totalChapters,
      total_worksheets: totalWorksheets,
      total_goals: totalGoals,
      students,
      worksheets_by_is_optional: optionalWorksheets,
      worksheets_by_location: worksheetsByLocation,
      worksheets_by_type: worksheetTypes,
      chapters,
      completion,
      optional_sheets: optionalSheets,
      speed_stats: speedStats,
      chapter_stats: chapterStats,
    } = programAnalyticsData || {};
    const totalChaptersGoalsWorksheets = [
      {
        label: 'Chapters',
        value: totalChapters,
      },
      {
        label: 'Goals',
        value: totalGoals,
      },
      {
        label: 'Worksheets',
        value: totalWorksheets,
      },
    ];
    const locationData = [
      {
        label: 'CW',
        value: getCountByLocation(LOCATION.CW, worksheetsByLocation),
      },
      {
        label: 'HW',
        value: getCountByLocation(LOCATION.HW, worksheetsByLocation),
      },
      {
        label: 'ANY',
        value: getCountByLocation(LOCATION.ANY, worksheetsByLocation),
      },
    ];
    const { total: totalStudents, active: activeStudents } =
      (students && students.find(item => item.type === TYPES.OVERALL)) || {};
    const studentsData =
      (students && students.filter(item => item.type !== TYPES.OVERALL)) || [];
    const { CW, HW } = getLocationBarPercentage(worksheetsByLocation) || {};
    const optionalSheetData = optionalSheetDataFormat(optionalSheets) || [];

    return (
      <Wrapper>
        {(programCode || programName) && (
          <WrapperHeadingText size="40px">{`${programCode} - ${programName}`}</WrapperHeadingText>
        )}
        <Separator transparent height="40" />
        <Row>
          {totalChaptersGoalsWorksheets.map(({ label, value }) => (
            <Box key={label}>
              <div>{label}</div>
              <div>{value}</div>
            </Box>
          ))}
        </Row>
        <Separator transparent height="40" />

        <HeadingText>Worksheets</HeadingText>
        <WorksheetContainer>
          <Chart worksheetTypes={worksheetTypes} />
          <WorksheetDataWrapper>
            <WorksheetLocationWrapper>
              <SubHeading top="10px">Worksheets by Location</SubHeading>
              <ProgressByLocation cw={CW} hw={HW} />
              <LocationTypes>
                {locationData.map(({ label, value }) => (
                  <ColBox key={label}>
                    <div>{label}</div>
                    <div>{value}</div>
                  </ColBox>
                ))}
              </LocationTypes>
            </WorksheetLocationWrapper>
            <OptionalWorksheetWrapper>
              {optionalWorksheets &&
                optionalWorksheets.map(({ type, count }) => (
                  <ColBox key={type}>
                    <div>{type === TYPES.MANDATORY ? 'Mandatory' : 'Optional'}</div>
                    <div>{count}</div>
                  </ColBox>
                ))}
            </OptionalWorksheetWrapper>
          </WorksheetDataWrapper>
        </WorksheetContainer>
        <Separator transparent height="40" />
        <HeadingText>Students</HeadingText>
        <Row>
          <Box>
            <div>{totalStudents}</div>
            <div>Assigned Students</div>
          </Box>
          <Box>
            <div>{activeStudents}</div>
            <div>Active Students</div>
          </Box>
        </Row>
        <SubHeading top="30px">
          Student Region
          {students && (
            <CsvIcon
              marginLeft={13}
              data={studentCsv(studentsData)}
              filename={'Student_data_'}
            >
              <i className="download icon"></i>
            </CsvIcon>
          )}
        </SubHeading>
        {students && <StudentTable studentData={studentsData} />}
        <Separator transparent height="40" />
        <HeadingWrapper alignItems="center">
          <HeadingText>Completion</HeadingText>
          {completion && (
            <CsvIcon marginTop={7} data={completion} filename={'Completion_data_'}>
              <i className="download icon"></i>
            </CsvIcon>
          )}
        </HeadingWrapper>
        {completion && <CompletionTable completionData={completion} />}
        <Separator transparent height="40" />
        {optionalSheets && (
          <>
            <SubHeading>
              Optional Sheets
              {students && (
                <CsvIcon
                  marginLeft={13}
                  data={optionalSheetCsv(optionalSheetData)}
                  filename={'Optional_sheet_Data'}
                >
                  <i className="download icon"></i>
                </CsvIcon>
              )}
            </SubHeading>
            <OptionalSheetTable optionalSheetsData={optionalSheetData} />
          </>
        )}
        <Separator transparent height="40" />
        <HeadingText>Speed</HeadingText>
        {speedStats && chapterStats && (
          <SpeedStatTable speedStatData={speedStats} chapterStatData={chapterStats} />
        )}
        <Separator transparent height="40" />
        <HeadingWrapper alignItems="center">
          <HeadingText>Chapters</HeadingText>
          {chapters && (
            <CsvIcon marginTop={7} data={chapters} filename={'Chapters_data_'}>
              <i className="download icon"></i>
            </CsvIcon>
          )}
        </HeadingWrapper>
        {chapters && <ChapterTable chaptersData={chapters} />}
        <Separator transparent height="40" />
      </Wrapper>
    );
  }
}

ProgramAnalytics.propTypes = {
  programId: PropTypes.string.isRequired,
  programAnalytics: PropTypes.shape({}),
  action: PropTypes.shape({
    getProgramAnalytics: PropTypes.func.isRequired,
  }),
};

ProgramAnalytics.defaultProps = {
  programAnalytics: null,
};

export default connect(
  ({ programAnalytics }) => ({
    programAnalytics: programAnalytics,
  }),
  dispatch => ({
    actions: bindActionCreators({ getProgramAnalytics }, dispatch),
  }),
)(ProgramAnalytics);
